/**
 * 收银台 → /api/pay/unified/execute 真实拉单与 OAuth 编排（SPA 专用，模板见 unifiedPay.html）。
 */
import { executeUnifiedPay } from '@/api/api'
import { buildQrImageSrc, computeQrPixelSize, isDenseQrText } from '@/utils/qrRender'

function normalizeExecuteError (err) {
  const d = err && err.data
  if (d && d.expired) {
    throw new Error(d.message || '订单已过期')
  }
  if (d && d.message) {
    throw new Error(d.message)
  }
  if (err && err.message) {
    throw new Error(err.message)
  }
  throw new Error('网络异常')
}

/** 拉单结果是否可供二维码 / 立即支付使用 */
export function isExecutePayloadReady (d) {
  if (!d || d.expired) {
    return false
  }
  return !!(
    (d.payUrl && String(d.payUrl).trim())
    || (d.codeUrl && String(d.codeUrl).trim())
    || (d.formContent && String(d.formContent).trim())
    || (d.codeImgUrl && String(d.codeImgUrl).trim())
    || (d.channelPayUrl && String(d.channelPayUrl).trim())
  )
}

export const CASHIER_EXPIRED_REDIRECT_URL = 'https://www.baidu.com'

/** 订单过期后用户仍尝试支付（扫码桥/立即支付）时统一跳转 */
export function redirectCashierExpiredPay () {
  if (typeof window === 'undefined') {
    return
  }
  window.location.replace(CASHIER_EXPIRED_REDIRECT_URL)
}

export function isExecuteExpiredPayload (d) {
  return !!(d && d.expired)
}

export function handleExecuteExpiredPayload (d) {
  if (!isExecuteExpiredPayload(d)) {
    return false
  }
  redirectCashierExpiredPay()
  return true
}

/** 建行龙支付 / 易捷石化钱包（SINOPEC ccb） */
export function isCcbPayType (payType) {
  return String(payType || '').trim().toLowerCase() === 'ccb'
}

export function isCcbBankPayUrl (url) {
  const u = String(url || '').trim().toLowerCase()
  return u.startsWith('mbspay://')
}

function isUnion95516WapUrl (url) {
  const u = String(url || '').trim().toLowerCase()
  return u.startsWith('https://') && u.includes('95516') && u.includes('redirectwapcashierdesk')
}

function isChinaPayOrderJson (text) {
  const s = String(text || '').trim()
  if (!s || s.charAt(0) !== '{') {
    return false
  }
  try {
    const o = JSON.parse(s)
    if (!o || typeof o !== 'object') {
      return false
    }
    const sig = o.Signature || o.signature
    return !!(o.TranType && o.MerOrderNo && sig)
  } catch (e) {
    return false
  }
}

/** 易捷银联 95516 / ChinaPay 外链不可付，不应走收银台展示 */
export const UNSUPPORTED_SINOPEC_UNION_PAY_MSG = '银联收银台外链不可用，请使用龙支付 mbspay 链'

export function isUnsupportedSinopecUnionPay (payload) {
  if (!payload) {
    return false
  }
  const fc = payload.formContent && String(payload.formContent).trim()
  if (fc && fc.indexOf('chinapayForm') >= 0) {
    return true
  }
  const candidates = [payload.channelPayUrl, payload.codeUrl, payload.payUrl]
  for (let i = 0; i < candidates.length; i++) {
    const v = String(candidates[i] || '').trim()
    if (isUnion95516WapUrl(v) || isChinaPayOrderJson(v)) {
      return true
    }
  }
  return false
}

/** page payType 或 execute 深链判定建行展示 */
export function resolveCcbPayPresentation (payType, payload) {
  if (isUnsupportedSinopecUnionPay(payload)) {
    return false
  }
  if (isCcbPayType(payType)) {
    return true
  }
  if (!payload) {
    return false
  }
  return isCcbBankPayUrl(pickMbspayPresentationUrl(payload))
}

/** 立即支付 / 深链唤起：优先 mbspay */
export function pickMbspayPresentationUrl (payload) {
  if (!payload) {
    return ''
  }
  const candidates = [payload.payUrl, payload.channelPayUrl, payload.codeUrl]
  for (let i = 0; i < candidates.length; i++) {
    const v = String(candidates[i] || '').trim()
    if (v && isCcbBankPayUrl(v)) {
      return v
    }
  }
  return ''
}

/** 二维码：优先 HTTPS codeUrl（建行桥接），兼容旧缓存 mbspay */
export function pickQrPresentationUrl (payload) {
  if (!payload) {
    return ''
  }
  const code = payload.codeUrl && String(payload.codeUrl).trim()
  if (code && !isCcbBankPayUrl(code)) {
    return code
  }
  const pay = pickMbspayPresentationUrl(payload)
  if (pay) {
    return pay
  }
  return code || ''
}

export function isJsapiConvertMode (ctx) {
  return !!(ctx && ctx.jsapiRequired && String(ctx.jsPayMode || '').toUpperCase() === 'CONVERT_H5')
}

/** 8007 立即支付：打开载体小程序 launch（render 包装） */
export function runJsapiImmediatePay (ctx) {
  const url = String(ctx && ctx.jsapiMpLaunchRenderUrl || ctx && ctx.jsapiMpLaunchScheme || '').trim()
  if (!url) {
    return Promise.reject(new Error('小程序唤起链接未配置，请刷新收银台'))
  }
  if (isAlipayWebView() || hasAlipayJsRuntime()) {
    window.location.href = url
    return Promise.resolve()
  }
  // 外链用通用扫一扫桥，勿复用 OAuth launcher（要求 payoauthinapplaunch）
  return openAlipayQrScanBridge(url)
}

/** convert 模式：从小程序返回后轮询 execute 缓存 */
export function pollJsapiConvertExecute (orderId, token) {
  return requestExecutePayload(orderId, token).then(function (d) {
    const pay = (d && d.payUrl && String(d.payUrl).trim())
      || (d && d.codeUrl && String(d.codeUrl).trim())
      || (d && d.channelPayUrl && String(d.channelPayUrl).trim())
    if (pay && !shouldBlockPayNavigation(pay)) {
      openPayNavigationUrl(pay, false)
      return true
    }
    return false
  })
}

export function setupJsapiConvertPoll (orderId, token, onHit) {
  if (typeof document === 'undefined') {
    return function () {}
  }
  let stopped = false
  let timer = null
  function tryPoll () {
    if (stopped) {
      return
    }
    pollJsapiConvertExecute(orderId, token).then(function (ok) {
      if (ok && typeof onHit === 'function') {
        onHit()
      }
    }).catch(function () {
      // ignore transient errors
    })
  }
  function onVis () {
    if (document.visibilityState === 'visible') {
      tryPoll()
    }
  }
  document.addEventListener('visibilitychange', onVis)
  timer = setInterval(tryPoll, 2500)
  return function teardown () {
    stopped = true
    if (timer) {
      clearInterval(timer)
    }
    document.removeEventListener('visibilitychange', onVis)
  }
}

export function requestExecutePayload (orderId, token) {
  return executeUnifiedPay(orderId, token).then(function (d) {
    if (!d) {
      throw new Error('服务异常')
    }
    if (handleExecuteExpiredPayload(d)) {
      return new Promise(function () {})
    }
    return d
  }).catch(function (err) {
    normalizeExecuteError(err)
  })
}

export function hasAlipayJsRuntime () {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    if (window.AlipayJSBridge) {
      return true
    }
    if (window.ap) {
      return true
    }
    if (window.my) {
      return true
    }
  } catch (e) {
    // ignore
  }
  return false
}

export function isAlipayWebView () {
  if (typeof navigator === 'undefined') {
    return false
  }
  const ua = (navigator.userAgent || '').toLowerCase()
  if (ua.indexOf('alipay') >= 0 || ua.indexOf('aliapp') >= 0 || ua.indexOf('nebula') >= 0) {
    return true
  }
  return hasAlipayJsRuntime()
}

function isAndroidBrowser () {
  if (typeof navigator === 'undefined') {
    return false
  }
  return /android/i.test(navigator.userAgent || '')
}

export function isDesktopBrowser () {
  if (typeof navigator === 'undefined') {
    return false
  }
  if (isAlipayWebView()) {
    return false
  }
  const ua = (navigator.userAgent || '').toLowerCase()
  return !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua)
}

export function isOAuthOrRenderBridgeUrl (url) {
  const lower = String(url || '').trim().toLowerCase()
  return lower.indexOf('render.alipay.com') >= 0
    || lower.indexOf('openauth.alipay.com') >= 0
    || lower.indexOf('openauth-sandbox') >= 0
}

/** payment 域买家 OAuth / 读秒：勿 JSBridge 20000067 直挂第三方 HTTPS */
export function isPaymentOAuthHttpsUrl (url) {
  const lower = String(url || '').trim().toLowerCase()
  if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
    return false
  }
  return lower.indexOf('payoauthinapplaunch') >= 0
    || lower.indexOf('payauthcallback') >= 0
    || lower.indexOf('uidoauthcountdown') >= 0
}

const RENDER_BRIDGE_PREFIX = 'https://render.alipay.com/p/s/i?scheme='
const ALIPAY_QR_SCAN_SA_ID = '10000007'

export function buildRenderBridgeUrl (httpsUrl) {
  const u = String(httpsUrl || '').trim()
  if (!u) {
    return ''
  }
  const lower = u.toLowerCase()
  if (lower.indexOf('render.alipay.com') >= 0) {
    return u
  }
  let deep = u
  if (isPaymentOAuthHttpsUrl(u)) {
    deep = 'alipays://platformapi/startapp?saId=' + ALIPAY_QR_SCAN_SA_ID
      + '&qrcode=' + encodeURIComponent(u)
  } else if (lower.startsWith('http://') || lower.startsWith('https://')) {
    deep = buildAlipayStartAppScheme(u)
  }
  if (!deep || deep === u) {
    return ''
  }
  return RENDER_BRIDGE_PREFIX + encodeURIComponent(deep)
}

/** 抖音商城短桥 HTTPS。仅安卓外置浏览器；苹果 / PC / 其它通道返回空。 */
function pickDouyinzfAndroidShortBridge (d) {
  if (!d || !isAndroidBrowser() || isAlipayWebView()) {
    return ''
  }
  const code = String(d.codeUrl || '').trim()
  const lower = code.toLowerCase()
  if (!(lower.startsWith('http://') || lower.startsWith('https://'))) {
    return ''
  }
  if (lower.indexOf('douyinzf-alipay-bridge') < 0) {
    return ''
  }
  return code
}

/**
 * 一点击：render 包扫码容器（saId=10000007&qrcode=短桥）。
 * 不用 appId=20000067：第三方短桥会被丢掉，只停在支付宝首页。
 */
export function resolveDouyinzfAndroidBridgeLaunchUrl (d) {
  const code = pickDouyinzfAndroidShortBridge(d)
  if (!code) {
    return ''
  }
  const scheme = 'alipays://platformapi/startapp?saId=' + ALIPAY_QR_SCAN_SA_ID
    + '&qrcode=' + encodeURIComponent(code)
  return RENDER_BRIDGE_PREFIX + encodeURIComponent(scheme)
}

/** 兜底二维码内容：短桥 HTTPS，进支付宝后 tradePay。 */
export function resolveDouyinzfAndroidQrUrl (d) {
  return pickDouyinzfAndroidShortBridge(d)
}

/**
 * 扫码/立即支付是否走买家 OAuth 编排（launcher 等待页或就地 getAuthCode）。
 * @param {{ buyerOAuthRequired?: boolean, buyerOAuthOpenUrl?: string, buyerOAuthInAppLaunchUrl?: string, buyerOAuthPullIsvAppId?: string, buyerOAuthProcessUrl?: string, token?: string, buyerOAuthStateConsumed?: boolean, orderPaying?: boolean }} ctx
 */
export function shouldPreferInAppOAuthLaunch (ctx) {
  if (!ctx || !ctx.buyerOAuthRequired || ctx.buyerOAuthStateConsumed || ctx.orderPaying) {
    return false
  }
  const launch = String(ctx.buyerOAuthInAppLaunchUrl || '').trim()
  const hasLaunch = launch && launch.toLowerCase().indexOf('payoauthinapplaunch') >= 0
  const open = String(ctx.buyerOAuthOpenUrl || '').trim()
  const openIsLauncher = open && open.toLowerCase().indexOf('payoauthinapplaunch') >= 0
  const openIsOpenauth = open && open.toLowerCase().indexOf('openauth') >= 0
  const hasInPageOAuth = !!(String(ctx.buyerOAuthPullIsvAppId || '').trim()
    && String(ctx.buyerOAuthProcessUrl || '').trim()
    && String(ctx.token || '').trim())
  return !!(hasLaunch || openIsLauncher || openIsOpenauth || hasInPageOAuth)
}

function openAlipaySchemeWithoutReferrer (scheme) {
  const u = String(scheme || '').trim()
  if (!u) {
    return Promise.reject(new Error('授权链接未配置'))
  }
  try {
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = u
    document.body.appendChild(iframe)
  } catch (e) {
    // ignore
  }
  return new Promise(function (resolve) {
    setTimeout(function () {
      window.location.href = u
      resolve()
    }, 200)
  })
}

/** rain81：openauth 二维码内容（裸 HTTPS，非 deep link） */
export function buyerOAuthQrFallback (ctx) {
  const open = String(ctx && ctx.buyerOAuthOpenUrl || '').trim()
  if (open) {
    return open
  }
  const scheme = String(ctx && ctx.buyerOAuthExternalLaunchScheme || '').trim()
  if (scheme) {
    return scheme
  }
  return String(ctx && ctx.buyerOAuthInAppLaunchUrl || '').trim()
}

export function shouldBlockPayNavigation (url) {
  if (!isAlipayWebView()) {
    return false
  }
  const lower = String(url || '').trim().toLowerCase()
  if (lower.indexOf('payauthcallback') >= 0 && (lower.indexOf('auth_code=') >= 0 || lower.indexOf('openauth') >= 0)) {
    return true
  }
  return isOAuthOrRenderBridgeUrl(url)
}

export function buildAlipayQrScanBridgeScheme (httpsUrl) {
  const u = String(httpsUrl || '').trim()
  if (!u) {
    return ''
  }
  const lower = u.toLowerCase()
  if (lower.startsWith('alipayqr://') || lower.startsWith('alipays://')) {
    return u
  }
  if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
    return ''
  }
  return 'alipayqr://platformapi/startapp?saId=' + ALIPAY_QR_SCAN_SA_ID
    + '&qrcode=' + encodeURIComponent(u)
}

/** rain81：openauth/render/payment OAuth 走扫一扫，勿 20000067 直挂 jqpay */
export function shouldUseAlipayQrScanBridge (url) {
  const lower = String(url || '').trim().toLowerCase()
  if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
    return false
  }
  return isPaymentOAuthHttpsUrl(url) || isOAuthOrRenderBridgeUrl(url)
}

const ALIPAY_H5_CONTAINER_APP_ID = '20000067'

/** HTTPS → alipays H5 容器 appId=20000067 */
export function buildAlipayStartAppScheme (httpsUrl) {
  const u = String(httpsUrl || '').trim()
  if (!u) {
    return ''
  }
  const lower = u.toLowerCase()
  if (lower.startsWith('alipays://') || lower.startsWith('alipayqr://')) {
    return u
  }
  if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
    return ''
  }
  return 'alipays://platformapi/startapp?appId=' + ALIPAY_H5_CONTAINER_APP_ID
    + '&url=' + encodeURIComponent(u)
}

function buildAlipayInAppBridgeScheme (httpsUrl) {
  if (shouldUseAlipayQrScanBridge(httpsUrl)) {
    return buildAlipayQrScanBridgeScheme(httpsUrl)
  }
  return buildAlipayStartAppScheme(httpsUrl)
}

/** rain81 openWithoutReferrer：iframe + assign alipayqr scheme */
export function openAlipayQrScanBridge (httpsUrl) {
  let entry = String(httpsUrl || '').trim()
  if (isPaymentOAuthHttpsUrl(entry)) {
    const render = buildRenderBridgeUrl(entry)
    if (render) {
      entry = render
    }
  }
  const scheme = buildAlipayQrScanBridgeScheme(entry)
  if (!scheme) {
    return Promise.reject(new Error('跳转地址无效'))
  }
  try {
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = scheme
    document.body.appendChild(iframe)
  } catch (e) {
    // ignore
  }
  return new Promise(function (resolve) {
    setTimeout(function () {
      window.location.assign(scheme)
      resolve()
    }, 200)
  })
}

function tryAlipayJsBridgeStartApp (httpsUrl) {
  const bridge = typeof window !== 'undefined' ? window.AlipayJSBridge : null
  if (!bridge || !bridge.call) {
    return false
  }
  bridge.call('startApp', {
    appId: ALIPAY_H5_CONTAINER_APP_ID,
    param: { url: httpsUrl }
  })
  return true
}

/** 仅 scheme / JSBridge 打开目标，绝不 location.href = https */
function assignAlipaySchemeBridge (httpsUrl) {
  const scheme = buildAlipayInAppBridgeScheme(httpsUrl)
  if (!scheme) {
    throw new Error('跳转地址无效')
  }
  window.location.assign(scheme)
}

/**
 * 支付宝内打开 HTTPS：
 * - payment OAuth 桥接页：必须 20000067（与模板一致），禁止 alipayqr（会弹「即将离开」并带 flowT）
 * - 其它：先 JSBridge 再 scheme
 */
export function openHttpsInAlipayBridge (httpsUrl) {
  const target = String(httpsUrl || '').trim()
  if (!target) {
    return Promise.reject(new Error('跳转地址无效'))
  }
  const lower = target.toLowerCase()
  if (lower.startsWith('alipays://') || lower.startsWith('alipayqr://')) {
    window.location.assign(target)
    return Promise.resolve()
  }
  if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
    return Promise.reject(new Error('跳转地址无效'))
  }
  if (shouldUseAlipayQrScanBridge(target)) {
    // 已在支付宝：20000067 打开，勿 alipayqr（会弹「即将离开」并带 flowT）
    if (isAlipayWebView() || hasAlipayJsRuntime()) {
      if (tryAlipayJsBridgeStartApp(target)) {
        return Promise.resolve()
      }
      const scheme = buildAlipayStartAppScheme(target)
      if (!scheme) {
        return Promise.reject(new Error('跳转地址无效'))
      }
      window.location.assign(scheme)
      return Promise.resolve()
    }
    return openAlipayQrScanBridge(target)
  }
  return new Promise(function (resolve, reject) {
    let done = false
    function finish (err) {
      if (done) {
        return
      }
      done = true
      if (err) {
        reject(err)
      } else {
        resolve()
      }
    }
    function attempt () {
      if (isPaymentOAuthHttpsUrl(target)) {
        return false
      }
      if (tryAlipayJsBridgeStartApp(target)) {
        finish()
        return true
      }
      return false
    }
    if (attempt()) {
      return
    }
    function onBridgeReady () {
      if (attempt()) {
        return
      }
      try {
        assignAlipaySchemeBridge(target)
        finish()
      } catch (e) {
        finish(e)
      }
    }
    document.addEventListener('AlipayJSBridgeReady', onBridgeReady, { once: true })
    setTimeout(function () {
      if (done) {
        return
      }
      if (attempt()) {
        return
      }
      try {
        assignAlipaySchemeBridge(target)
        finish()
      } catch (e) {
        finish(e)
      }
    }, 600)
  })
}

/** 支付宝内打开支付/收银台链接（仅桥接；非支付宝 WebView 才允许普通 href/replace） */
export function openPayNavigationUrl (url, useReplace) {
  const u = String(url || '').trim()
  if (!u) {
    return Promise.reject(new Error('链接无效'))
  }
  if (isUnion95516WapUrl(u) || isChinaPayOrderJson(u)) {
    return Promise.reject(new Error(UNSUPPORTED_SINOPEC_UNION_PAY_MSG))
  }
  if (isCcbBankPayUrl(u)) {
    try {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = u
      document.body.appendChild(iframe)
    } catch (e) {
      // ignore
    }
  }
  if (isAlipayWebView()) {
    const lower = u.toLowerCase()
    if (lower.startsWith('http://') || lower.startsWith('https://')) {
      return openHttpsInAlipayBridge(u)
    }
    if (lower.startsWith('alipays://') || lower.startsWith('alipayqr://') || isCcbBankPayUrl(u)) {
      window.location.assign(u)
      return Promise.resolve()
    }
    return Promise.reject(new Error('当前环境不支持该链接类型'))
  }
  if (useReplace) {
    window.location.replace(u)
  } else {
    window.location.href = u
  }
  return Promise.resolve()
}

/**
 * 外链：alipayqr 桥接 payOAuthInAppLaunch（与模板 openExternalBrowserToAlipayLaunch 对齐）。
 */
export function openExternalBrowserToAlipayLaunch (buyerOAuthInAppLaunchUrl) {
  const httpsUrl = String(buyerOAuthInAppLaunchUrl || '').trim()
  if (!httpsUrl || httpsUrl.toLowerCase().indexOf('payoauthinapplaunch') < 0) {
    return Promise.reject(new Error('授权桥接地址未配置'))
  }
  return openAlipayQrScanBridge(httpsUrl)
}

/**
 * 外链兜底：优先 external scheme / openUrl 的 alipayqr，否则回落 launcher。
 */
export function openExternalBrowserToAlipayOAuth (buyerOAuthExternalLaunchScheme, buyerOAuthInAppLaunchUrl, buyerOAuthOpenUrl) {
  let scheme = String(buyerOAuthExternalLaunchScheme || '').trim()
  if (!scheme) {
    const openUrl = String(buyerOAuthOpenUrl || '').trim()
    if (openUrl) {
      scheme = buildAlipayQrScanBridgeScheme(openUrl)
    }
  }
  if (scheme) {
    return openAlipaySchemeWithoutReferrer(scheme)
  }
  const httpsUrl = String(buyerOAuthInAppLaunchUrl || '').trim()
  if (httpsUrl && httpsUrl.toLowerCase().indexOf('payoauthinapplaunch') >= 0) {
    return openExternalBrowserToAlipayLaunch(httpsUrl)
  }
  return Promise.reject(new Error('授权链接未配置'))
}

/** 支付宝内兜底：20000067 打开 payOAuthInAppLaunch（须已配置 H5 白名单） */
export function runInAppBuyerOAuth (buyerOAuthInAppLaunchUrl) {
  const url = String(buyerOAuthInAppLaunchUrl || '').trim()
  if (!url) {
    return Promise.reject(new Error('授权桥接地址未配置'))
  }
  const lower = url.toLowerCase()
  if (lower.indexOf('auth_code=') >= 0) {
    return Promise.reject(new Error('授权地址异常'))
  }
  if (lower.indexOf('payoauthinapplaunch') < 0 && lower.indexOf('payauthcallback') >= 0) {
    return Promise.reject(new Error('授权桥接地址异常，请刷新收银台'))
  }
  // 同单只唤起一次 20000067，避免「两次离开支付宝」+ 双开 launcher
  try {
    let stateKey = ''
    try {
      stateKey = new URL(url, window.location.href).searchParams.get('state') || ''
    } catch (e) {
      stateKey = ''
    }
    const lockKey = 'oauthLaunchOnce:' + (stateKey || url.slice(0, 120))
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem(lockKey) === '1') {
        return Promise.resolve()
      }
      sessionStorage.setItem(lockKey, '1')
    }
  } catch (e) {
    // ignore
  }
  if (isPaymentOAuthHttpsUrl(url)) {
    if (tryAlipayJsBridgeStartApp(url)) {
      return Promise.resolve()
    }
    const scheme = buildAlipayStartAppScheme(url)
    if (!scheme) {
      return Promise.reject(new Error('跳转地址无效'))
    }
    window.location.assign(scheme)
    return Promise.resolve()
  }
  return openHttpsInAlipayBridge(url)
}
const ALIPAY_JSAPI_SRC = 'https://gw.alipayobjects.com/as/g/h5-lib/alipayjsapi/3.1.1/alipayjsapi.min.js'

function ensureAlipayJsApiLoaded () {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('环境异常'))
  }
  if (window.ap && window.ap.getAuthCode) {
    return Promise.resolve()
  }
  return new Promise(function (resolve, reject) {
    const existing = document.querySelector('script[data-alipay-jsapi="1"]')
    if (existing) {
      existing.addEventListener('load', function () { resolve() })
      existing.addEventListener('error', function () { reject(new Error('支付组件加载失败')) })
      return
    }
    const script = document.createElement('script')
    script.src = ALIPAY_JSAPI_SRC
    script.setAttribute('data-alipay-jsapi', '1')
    script.onload = function () { resolve() }
    script.onerror = function () { reject(new Error('支付组件加载失败')) }
    document.head.appendChild(script)
  })
}

/** 与 oauthCallbackPending / oauthCallbackLauncher / alipaySchemeRedirect 文案对齐 */
export const OAUTH_ENV_HINT = '正在检测环境，请勿关闭页面'
export const OAUTH_ENV_HINT_HLD_STEP1 = '第一步：正在授权，请勿关闭页面'

export function resolveOAuthEnvHint (ctx) {
  if (ctx && ctx.hldChannel) {
    return OAUTH_ENV_HINT_HLD_STEP1
  }
  const channelId = ctx && (ctx.channelId != null ? String(ctx.channelId) : '')
  const channelName = String(ctx && (ctx.channelName || ctx.pluginName || '') || '').toLowerCase()
  if (channelId === '10005' || channelName.includes('hld') || channelName.includes('3vast') || channelName.includes('慧徕')) {
    return OAUTH_ENV_HINT_HLD_STEP1
  }
  return OAUTH_ENV_HINT
}

export const OAUTH_PROCESS_LOADING = {
  detect: { title: OAUTH_ENV_HINT, sub: OAUTH_ENV_HINT },
  connect: { title: OAUTH_ENV_HINT, sub: OAUTH_ENV_HINT },
  auth: { title: OAUTH_ENV_HINT, sub: OAUTH_ENV_HINT },
  prepare: { title: OAUTH_ENV_HINT, sub: OAUTH_ENV_HINT }
}

export function resolveOAuthProcessLoading (ctx) {
  const hint = resolveOAuthEnvHint(ctx)
  return {
    detect: { title: hint, sub: hint },
    connect: { title: hint, sub: hint },
    auth: { title: hint, sub: hint },
    prepare: { title: hint, sub: hint }
  }
}

function notifyOAuthProgress (onProgress, phase) {
  if (typeof onProgress !== 'function' || !phase) {
    return
  }
  onProgress(phase)
}

/**
 * 就地 getAuthCode 后直跳 /process（与 launcher 一致），避免再经 pending 多一次整页跳转。
 * 等待遮罩保留至浏览器换页。
 */
function navigateOAuthProcess (ctx, state, authCode, onProgress) {
  const loading = resolveOAuthProcessLoading(ctx)
  notifyOAuthProgress(onProgress, loading.connect)
  const processUrl = String(ctx && ctx.buyerOAuthProcessUrl || '').trim()
  const pullIsvAppId = String(ctx && ctx.buyerOAuthPullIsvAppId || '').trim()
  if (!processUrl) {
    return Promise.reject(new Error('授权处理地址未配置'))
  }
  const u = new URL(processUrl, window.location.href)
  u.searchParams.set('auth_code', authCode)
  u.searchParams.set('state', state)
  if (pullIsvAppId) {
    u.searchParams.set('app_id', pullIsvAppId)
  }
  u.searchParams.set('source', 'alipay_wallet')
  u.searchParams.set('scope', 'auth_base')
  window.location.assign(u.href)
  return Promise.resolve()
}

/** 仅 ap.getAuthCode；勿先打 AlipayJSBridge.call（易弹「服务正忙」，绕黄页仍靠 20000067）。 */
function invokeGetAuthCode (pullIsvAppId) {
  return new Promise(function (resolve, reject) {
    var started = false
    var fallbackTimer = null
    function onCode (res) {
      if (res && res.authCode) {
        resolve(res.authCode)
        return
      }
      reject(new Error((res && res.errorMessage) ? res.errorMessage : '授权失败'))
    }
    function runAp () {
      if (typeof window.ap === 'undefined' || !window.ap.getAuthCode) {
        reject(new Error('请完成授权'))
        return
      }
      window.ap.getAuthCode({
        appId: pullIsvAppId,
        scopes: ['auth_base']
      }, onCode)
    }
    function startOnce (runner) {
      if (started) {
        return
      }
      started = true
      if (fallbackTimer != null) {
        clearTimeout(fallbackTimer)
        fallbackTimer = null
      }
      runner()
    }
    if (window.ap && window.ap.ready) {
      window.ap.ready(function () { startOnce(runAp) })
      return
    }
    document.addEventListener('AlipayJSBridgeReady', function () { startOnce(runAp) }, { once: true })
    fallbackTimer = setTimeout(function () { startOnce(runAp) }, 600)
  })
}

/**
 * rain81：buyerOAuthOpenUrl 为裸 openauth。此类订单禁止 getAuthCode→/process，
 * 否则会与 openauth→payAuthCallback 双开，出现两个 auth_code、两次「离开支付宝」。
 */
function isRain81OpenAuthOpenUrl (ctxOrOpenUrl) {
  const open = typeof ctxOrOpenUrl === 'string'
    ? ctxOrOpenUrl
    : String(ctxOrOpenUrl && ctxOrOpenUrl.buyerOAuthOpenUrl || '')
  const openLower = open.trim().toLowerCase()
  return openLower.indexOf('openauth.alipay') >= 0
    || openLower.indexOf('openauth-sandbox') >= 0
}

/**
 * /process 与当前收银台同域时，才允许就地 getAuthCode 后 location 跳转。
 * SPA 常在独立收银台域，跨域跳 payment /process 会弹「即将离开支付宝」——应改走 20000067 桥接页。
 */
function isSameOriginOAuthProcessUrl (processUrl) {
  const raw = String(processUrl || '').trim()
  if (!raw || typeof window === 'undefined' || !window.location) {
    return false
  }
  try {
    const u = new URL(raw, window.location.href)
    return u.origin === window.location.origin
  } catch (e) {
    return false
  }
}

function canRunBuyerOAuthOnCurrentPage (ctx) {
  if (isRain81OpenAuthOpenUrl(ctx)) {
    return false
  }
  return !!(String(ctx && ctx.buyerOAuthPullIsvAppId || '').trim()
    && String(ctx && ctx.buyerOAuthProcessUrl || '').trim()
    && String(ctx && ctx.token || '').trim()
    && isSameOriginOAuthProcessUrl(ctx.buyerOAuthProcessUrl))
}

/**
 * 支付宝内已在收银台页：就地 getAuthCode → 直跳 /process。
 * 勿再 HTTPS 打开 payOAuthInAppLaunch（易弹「离开支付宝」）。
 * @param {{ buyerOAuthPullIsvAppId?: string, buyerOAuthProcessUrl?: string, token?: string, buyerOAuthOpenUrl?: string }} ctx
 * @param {{ onProgress?: (phase:{title:string,sub:string})=>void }} [opts]
 */
export function runBuyerOAuthOnCurrentPage (ctx, opts) {
  const pullIsvAppId = String(ctx && ctx.buyerOAuthPullIsvAppId || '').trim()
  const state = String(ctx && ctx.token || '').trim()
  const onProgress = opts && opts.onProgress
  if (isRain81OpenAuthOpenUrl(ctx)) {
    return Promise.reject(new Error('当前通道请使用扫码授权，勿就地 getAuthCode'))
  }
  if (!pullIsvAppId || !state || !canRunBuyerOAuthOnCurrentPage(ctx)) {
    return Promise.reject(new Error('授权参数未配置，请刷新收银台'))
  }
  notifyOAuthProgress(onProgress, resolveOAuthProcessLoading(ctx).auth)
  return ensureAlipayJsApiLoaded()
    .then(function () {
      return invokeGetAuthCode(pullIsvAppId)
    })
    .then(function (authCode) {
      return navigateOAuthProcess(ctx, state, authCode, onProgress)
    })
}

/**
 * 立即支付 OAuth：
 * - rain81（buyerOAuthOpenUrl 含 openauth）：支付宝内直跳裸 openauth；外链走 alipayqr(openauth)
 * - 同域收银台：可就地 getAuthCode → /process
 * - 跨域 SPA（HLD/聚合）：支付宝内 20000067 进桥接页；外链 alipayqr 进 launcher
 */
export function runBuyerOAuthForImmediatePay (ctx, opts) {
  if (!shouldPreferInAppOAuthLaunch(ctx)) {
    if (ctx && ctx.buyerOAuthRequired && !ctx.buyerOAuthStateConsumed && !ctx.orderPaying) {
      return Promise.reject(new Error('授权链接未配置，请刷新收银台或联系商户'))
    }
    return Promise.reject(new Error('当前订单无需授权'))
  }
  const openUrl = String(ctx && ctx.buyerOAuthOpenUrl || '').trim()
  const isRain81OpenAuth = isRain81OpenAuthOpenUrl(openUrl)
  const launch = String(ctx && ctx.buyerOAuthInAppLaunchUrl || '').trim()
  const inAlipay = isAlipayWebView() || hasAlipayJsRuntime()
  const onProgress = opts && opts.onProgress

  // rain81：只走裸 openauth，禁止再 getAuthCode / launcher（双开「离开」+ 双 auth_code）
  if (isRain81OpenAuth) {
    notifyOAuthProgress(onProgress, resolveOAuthProcessLoading(ctx).auth)
    if (!inAlipay) {
      return openExternalBrowserToAlipayOAuth(
        ctx.buyerOAuthExternalLaunchScheme,
        ctx.buyerOAuthInAppLaunchUrl,
        openUrl
      )
    }
    window.location.assign(openUrl)
    return Promise.resolve()
  }

  // 仅同域才就地授权；跨域就地跳 /process 反而更爱弹「即将离开」
  if (inAlipay && canRunBuyerOAuthOnCurrentPage(ctx)) {
    return runBuyerOAuthOnCurrentPage(ctx, opts)
  }
  if (!inAlipay) {
    if (launch && launch.toLowerCase().indexOf('payoauthinapplaunch') >= 0) {
      return openExternalBrowserToAlipayLaunch(launch)
    }
    return openExternalBrowserToAlipayOAuth(
      ctx.buyerOAuthExternalLaunchScheme,
      ctx.buyerOAuthInAppLaunchUrl,
      ctx.buyerOAuthOpenUrl
    )
  }
  // 支付宝内（含跨域 SPA）：20000067 打开桥接页，勿 alipayqr
  if (launch && launch.toLowerCase().indexOf('payoauthinapplaunch') >= 0) {
    notifyOAuthProgress(onProgress, resolveOAuthProcessLoading(ctx).auth)
    return runInAppBuyerOAuth(launch)
  }
  return Promise.reject(new Error('授权参数未配置，请刷新收银台'))
}

function applyPayload (d, dom) {
  if (!d) {
    return false
  }
  if (d.formContent && String(d.formContent).trim()) {
    document.open()
    document.write(d.formContent)
    document.close()
    return true
  }
  if (d.payUrl && String(d.payUrl).trim()) {
    if (shouldBlockPayNavigation(d.payUrl)) {
      return false
    }
    openPayNavigationUrl(String(d.payUrl).trim(), false)
    return true
  }
  if (d.codeImgUrl && String(d.codeImgUrl).trim() && dom && dom.setQrImg) {
    dom.setQrImg(String(d.codeImgUrl).trim(), false)
    return true
  }
  return false
}

/** alipays:// / alipayqr:// 唤起 App，当前 WebView 不会卸载，须主动关闭 loading */
export function isAlipayAppDeepLink (url) {
  const u = String(url || '').trim().toLowerCase()
  return u.startsWith('alipays://') || u.startsWith('alipayqr://')
}

/**
 * 「立即支付」成功后是否应关闭 loading 并恢复按钮。
 * 仅表单写页保持 loading（整页将被 form 替换）；https / 深链跳转后应关闭，避免卡在假进度。
 */
export function shouldDismissPayLoadingAfterImmediatePay (d) {
  if (!d) {
    return true
  }
  if (d.formContent && String(d.formContent).trim()) {
    return false
  }
  return true
}

/** 支付宝内禁止解开 render 后跳转 openauth */
export function wrapPayUrlForExternalOpen (url) {
  const u = String(url || '').trim()
  if (!u || shouldBlockPayNavigation(u)) {
    return ''
  }
  return u
}

/**
 * 立即支付导航：与总后台 sys.lklmp.immediate.launch.mode 对齐。
 * <ul>
 * <li>juhe：聚合 payUrl 原样透传（禁止二次 encode）</li>
 * <li>yuansheng：qr.alipay.com/bax 按 render 包装（支付宝 WebView 内仍用短链）</li>
 * </ul>
 * 扫码二维码仍用原始短链，勿走本函数。
 * @param {string} url
 * @param {string} [mode] juhe | yuansheng（后端已 normalize；旧 render/scheme 等不会下发）
 */
export function resolveQrAlipayImmediatePayUrl (url, mode) {
  const u = String(url || '').trim()
  if (!u) {
    return ''
  }
  const m = String(mode || 'yuansheng').trim().toLowerCase()
  // 聚合收银台：整段 payUrl 原样打开（token 含 ==，二次 encode 会 token 解析异常）
  if (m === 'juhe' || m === '聚合' || m === 'aggregate' || m === 'payurl') {
    return u
  }
  const lower = u.toLowerCase()
  const isQrAlipay = lower.indexOf('https://qr.alipay.com/') === 0
    || lower.indexOf('http://qr.alipay.com/') === 0
  if (!isQrAlipay) {
    return u
  }
  if (isAlipayWebView() || hasAlipayJsRuntime()) {
    return u
  }
  // yuansheng（默认）：外链 render 桥
  const render = buildRenderBridgeUrl(u)
  return render || u
}

/** 手机「立即支付」：优先跳转链，表单兜底（与模板 applyImmediatePay 一致） */
export function applyImmediatePay (d, dom, oauthCtx, launchMode) {
  if (!d) {
    return false
  }
  if (handleExecuteExpiredPayload(d)) {
    return true
  }
  if (isUnsupportedSinopecUnionPay(d)) {
    return false
  }
  const ctx = typeof oauthCtx === 'boolean'
    ? { buyerOAuthRequired: oauthCtx }
    : (oauthCtx || {})
  if (shouldPreferInAppOAuthLaunch(ctx)) {
    return false
  }
  const mode = (d && d.qrAlipayImmediateLaunchMode) || launchMode || 'yuansheng'
  const navUrl = pickMbspayPresentationUrl(d)
    || (d.payUrl && String(d.payUrl).trim())
    || (d.channelPayUrl && String(d.channelPayUrl).trim())
    || ''
  if (isCcbBankPayUrl(navUrl)) {
    openPayNavigationUrl(navUrl, false)
    return true
  }
  if (d.payUrl && String(d.payUrl).trim()) {
    const pay = wrapPayUrlForExternalOpen(String(d.payUrl).trim())
    if (!pay) {
      return false
    }
    openPayNavigationUrl(resolveQrAlipayImmediatePayUrl(pay, mode), false)
    return true
  }
  if (d.codeUrl && String(d.codeUrl).trim()) {
    const code = wrapPayUrlForExternalOpen(String(d.codeUrl).trim())
    if (!code) {
      return false
    }
    openPayNavigationUrl(resolveQrAlipayImmediatePayUrl(code, mode), false)
    return true
  }
  if (d.formContent && String(d.formContent).trim()) {
    document.open()
    document.write(d.formContent)
    document.close()
    return true
  }
  if (d.codeImgUrl && String(d.codeImgUrl).trim()) {
    return applyPayload(d, dom)
  }
  return false
}

/** alipays:// / alipayqr:// 唤起 App，当前 WebView 不会卸载，须主动关闭 loading */
export function resolveQrDisplay (payload, orderId, token, qrFallback, payType) {
  if (isUnsupportedSinopecUnionPay(payload)) {
    return Promise.resolve(null)
  }
  if (payload && payload.codeImgUrl && String(payload.codeImgUrl).trim()) {
    return Promise.resolve({
      src: String(payload.codeImgUrl).trim(),
      showCenterLogo: false
    })
  }
  const qrText = pickQrPresentationUrl(payload)
    || (qrFallback && String(qrFallback).trim())
    || ''
  if (!qrText || shouldBlockPayNavigation(qrText)) {
    return Promise.resolve(null)
  }
  const ccbMode = resolveCcbPayPresentation(payType, payload) || isCcbBankPayUrl(pickMbspayPresentationUrl(payload))
  const dense = isDenseQrText(qrText)
  const displayEdge = computeQrPixelSize(qrText)
  return buildQrImageSrc(qrText).then(function (src) {
    return src ? { src: src, showCenterLogo: !ccbMode, dense: dense, displayEdge: displayEdge } : null
  })
}

export function applyExpireUi (dom) {
  if (dom && dom.payCard) dom.payCard.classList.add('dim')
  if (dom && dom.hintExpired) dom.hintExpired.style.display = 'block'
  if (dom && dom.stampOverlay) dom.stampOverlay.style.display = 'flex'
  if (dom && dom.payActions) dom.payActions.style.display = 'none'
  if (dom && dom.btn) dom.btn.disabled = true
  if (dom && dom.ph) dom.ph.style.display = 'none'
}

const PAY_LOADING_HINTS = [
  '正在连接支付渠道…',
  '正在分配收款账号…',
  '请稍候，请勿关闭本页…',
  '马上跳转，请耐心等待…'
]

const DEFAULT_PAY_LOADING_TITLE = '正在为您分配收款账号'
const DEFAULT_PAY_LOADING_SUB = '请稍候，正在连接支付渠道…'

/** @param {{ setVisible: (boolean)=>void, setHint: (string)=>void, setTitle?: (string)=>void, setSub?: (string)=>void }} hooks */
export function createPayLoadingController (hooks) {
  let hintTimer = null
  let safetyTimer = null
  let hintIdx = 0

  function clearTimers () {
    if (hintTimer) {
      clearInterval(hintTimer)
      hintTimer = null
    }
    if (safetyTimer) {
      clearTimeout(safetyTimer)
      safetyTimer = null
    }
  }

  function applyPhase (phase) {
    if (!phase) {
      return
    }
    if (hooks && hooks.setTitle && phase.title) {
      hooks.setTitle(phase.title)
    }
    if (hooks && hooks.setSub && phase.sub) {
      hooks.setSub(phase.sub)
    }
    if (hooks && hooks.setHint) {
      hooks.setHint(phase.hint || phase.sub || '')
    }
  }

  return {
    /**
     * @param {{ safetyMs?: number, onSafetyTimeout?: ()=>void, initialHint?: string, title?: string, sub?: string, mode?: 'default'|'oauth' }} [opts]
     */
    show (opts) {
      const options = opts || {}
      if (hooks && hooks.setVisible) hooks.setVisible(true)
      document.body.style.overflow = 'hidden'
      clearTimers()
      hintIdx = 0
      const mode = options.mode || 'default'
      if (hooks && hooks.setOauthMode) hooks.setOauthMode(mode === 'oauth')
      if (mode === 'oauth') {
        applyPhase({
          title: options.title || OAUTH_PROCESS_LOADING.detect.title,
          sub: options.sub || OAUTH_PROCESS_LOADING.detect.sub,
          hint: options.initialHint || OAUTH_PROCESS_LOADING.detect.sub
        })
      } else {
        applyPhase({
          title: options.title || DEFAULT_PAY_LOADING_TITLE,
          sub: options.sub || DEFAULT_PAY_LOADING_SUB,
          hint: options.initialHint || PAY_LOADING_HINTS[0]
        })
        hintTimer = setInterval(function () {
          hintIdx = (hintIdx + 1) % PAY_LOADING_HINTS.length
          if (hooks && hooks.setHint) hooks.setHint(PAY_LOADING_HINTS[hintIdx])
        }, 2800)
      }
      const safetyMs = options.safetyMs != null ? options.safetyMs : 60000
      if (typeof options.onSafetyTimeout === 'function' && safetyMs > 0) {
        safetyTimer = setTimeout(options.onSafetyTimeout, safetyMs)
      }
    },
    /** @param {{ title?: string, sub?: string, hint?: string }} phase */
    setPhase (phase) {
      applyPhase(phase)
    },
    hide () {
      clearTimers()
      if (hooks && hooks.setOauthMode) hooks.setOauthMode(false)
      if (hooks && hooks.setVisible) hooks.setVisible(false)
      document.body.style.overflow = ''
    }
  }
}

export function loadPayloadForPay (getLatest, getPreloadPromise, orderId, token, forceRefresh) {
  if (!forceRefresh) {
    const latest = typeof getLatest === 'function' ? getLatest() : getLatest
    if (latest && isExecutePayloadReady(latest)) {
      return Promise.resolve(latest)
    }
    const preloadPromise = typeof getPreloadPromise === 'function' ? getPreloadPromise() : getPreloadPromise
    if (preloadPromise) {
      return preloadPromise.then(function () {
        const again = typeof getLatest === 'function' ? getLatest() : getLatest
        if (again) {
          return again
        }
        return requestExecutePayload(orderId, token)
      })
    }
  }
  return requestExecutePayload(orderId, token)
}
